<template>
  <div :id="id" class="text-base" :qaid="qaidPanel">
    <div class="p-1 md:p-2 text-left">
      <trek-button
        text
        icon-only
        icon="close"
        :aria-label="bp('closeSavedListTooltip')"
        class="p-25 absolute top-0 right-0 mt-2 mr-2"
        @click="closeTooltip"
      >
      </trek-button>

      <div class="section-header mb-2" :qaid="qaidTitle">
        <div class="heading text-xl">{{ bp('moveToList') }}</div>
      </div>

      <div v-if="userLists && userLists.length" class="b2b-scroll-list-container md:min-w-xs" :qaidList="qaidList">
        <div class="b2b-scroll-list">
          <div class="b2b-scroll-list__item b2b-scroll-list__item--category font-medium">{{ textMyLists }}</div>

          <div
            v-for="list in userLists"
            :key="list.id"
            class="b2b-scroll-list__item cursor-pointer"
            @click="onUpdate(list.id)"
            v-text="`${list.name} (${list.totalEntries})`"
          ></div>
        </div>
      </div>

      <div v-else class="callout callout--secondary">{{ bp('zeroLists') }}</div>

      <form class="mt-4" @submit.prevent="onCreate(listName)">
        <div class="form-group">
          <label class="form-label">{{ bp('createNewList') }}</label>
          <input
            id="create-new"
            v-model="listName"
            class="form-control"
            type="text"
            maxlength="40"
            :placeholder="bp('enterName')"
          />
          <span id="create-new-help" class="form-help">{{ textMaxChars }}</span>
        </div>

        <div class="buttons buttons--right">
          <trek-button primary type="submit" qaid="create-and-save-list-button" :disabled="!listName">
            <span v-html="bp('createSave')"></span>
          </trek-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import {mapState, mapActions, mapMutations} from 'vuex';
import {getToast} from '@/utils/axios';
import get from 'lodash/get';
import TrekButton from '@/components/TrekButton';

export default {
  components: {TrekButton},
  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    textMaxChars: {
      type: String,
      default: '',
    },
    textMyLists: {
      type: String,
      default: '',
    },
    textOtherLists: {
      type: String,
      default: '',
    },
    textMovedToList: {
      type: String,
      default: '',
    },
    textUndo: {
      type: String,
      default: '',
    },
    tippyId: {
      type: String,
      default: '',
    },
    btnId: {
      type: String,
      default: '',
    },
    qaidPanel: {
      type: String,
      default: '',
    },
    qaidTitle: {
      type: String,
      default: '',
    },
    qaidList: {
      type: String,
      default: '',
    },
    token: {
      type: String,
      default: '',
    },
    showMinQtyToasts: Boolean,
    isBuyingZone: Boolean,
    id: {
      type: String,
      default: 'saved-lists',
    },
    onSubmitCallback: {
      type: Function,
      default: () => {},
    },
    onUndoCallback: {
      type: Function,
      default: () => {},
    },
    partTypeKey: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      listName: null,
    };
  },

  computed: {
    ...mapState('savedLists', ['userLists', 'accountLists', 'entries', 'activeEntries', 'isFetchedAllOnce']),
  },

  created() {
    // Prevent multiple fetching of saved lists.
    if (!this.isFetchedAllOnce) {
      this.fetchAllLists();
    }
  },

  methods: {
    onCreate(name) {
      this.setIsBuyingZone(this.isBuyingZone);
      this.identifyEntriesList();
      const csrfToken = this.token;

      this.setUseToastInterceptor(false);

      this.createList({name, csrfToken}).then((data) => {
        const failedQtySKUs = (get(data, 'data.failedSKUs') || []).map((item) => item.sku);
        const failedQtyMessages = (get(data, 'data.failedSKUs') || []).map((item) => item.message.basePropertyValue);

        this.$emit('failed-skus', failedQtySKUs, failedQtyMessages);

        const {type, message} = getToast(data.meta.feedback);

        const {id, entries} = data.data;
        const undoProps = entries
          ? {
              actionClickText: this.textUndo,
              actionClick: () => this.undoCreate(id),
            }
          : {};
        this.listName = null;

        let qtyToastMessage = this.showMinQtyToasts && failedQtyMessages ? failedQtyMessages[0] : null;

        // Since for BBD-4667, min Qty error messages arrive in data.failedSkus rather than meta,
        // we cannot use the routine meta message checker.

        if (type !== 'alert') {
          this.$notify({
            type,
            message,
            ...undoProps,
          });
        } else if (qtyToastMessage) {
          this.$notify({
            type: type,
            message: qtyToastMessage,
          });
        } else if (message) {
          this.$notify({
            type,
            message,
            ...undoProps,
          });
        }

        this.clearQty();
        this.closeTooltip();
        this.setUseToastInterceptor(true);

        if (!(failedQtySKUs && failedQtySKUs.length)) {
          if (this.onSubmitCallback) {
            this.onSubmitCallback(this.partTypeKey || entries);
          }
        }
      });
    },

    undoCreate(id) {
      this.deleteList(id).then((data) => {
        if (this.onUndoCallback) {
          this.onUndoCallback(data.data.data.entries);
        }
      });
    },

    onUpdate(id) {
      this.setUseToastInterceptor(false);
      this.setIsBuyingZone(this.isBuyingZone);
      this.identifyEntriesList();
      this.updateListEntries({id, entries: this.activeEntries}).then((data) => {
        const failedQtySKUs = (get(data, 'data.failedSKUs') || []).map((item) => item.sku);
        const failedQtyMessages = (get(data, 'data.failedSKUs') || []).map((item) => item.message.basePropertyValue);

        this.$emit('failed-skus', failedQtySKUs, failedQtyMessages);

        const {type, message} = getToast(data.meta.feedback);
        const {id, entries} = data.data;

        const undoProps = {
          actionClickText: this.textUndo,
          actionClick: () => this.undoUpdate(id, entries),
        };

        let qtyToastMessage = this.showMinQtyToasts && failedQtyMessages ? failedQtyMessages[0] : null;

        // Since for BBD-4667, min Qty error messages arrive in data.failedSkus rather than meta,
        // we cannot use the routine meta message checker.

        if (type !== 'alert') {
          this.$notify({
            type,
            message,
            ...undoProps,
          });
        } else if (qtyToastMessage) {
          this.$notify({
            type: type,
            message: qtyToastMessage,
          });
        } else if (message) {
          this.$notify({
            type,
            message,
            ...undoProps,
          });
        }

        this.clearQty();
        this.closeTooltip();
        this.setUseToastInterceptor(true);

        if (!(failedQtySKUs && failedQtySKUs.length)) {
          if (this.onSubmitCallback) {
            this.onSubmitCallback(this.partTypeKey || entries);
          }
        }
      });
    },

    undoUpdate(id, undoEntries) {
      this.setUseToastInterceptor(false);
      this.fetchList(id).then((data) => {
        const existingEntries = [...data.data.bikesEntries, ...data.data.partsEntries];
        const updatedEntries = existingEntries.reduce((acc, entry) => {
          const matched = undoEntries.find((undoEntry) => undoEntry.sku === entry.sku);

          if (matched) {
            const newQty = entry.orderedQty - matched.qty;

            acc.push({
              sku: matched.sku,
              qty: newQty <= 0 ? 0 : newQty,
            });
          }

          return acc;
        }, []);

        this.setUseToastInterceptor(true);
        this.updateSelectedEntries({id, entries: updatedEntries}).then((data) => {
          const dataEntries = get(data, 'data.entries') || [];
          if (this.onUndoCallback) {
            this.onUndoCallback(dataEntries);
          }
        });
      });
    },

    closeTooltip() {
      this.listName = null;
      const tippyEl = document.getElementById(this.btnId);

      if (tippyEl && tippyEl._tippy) {
        tippyEl._tippy.hide();
      }
    },

    ...mapMutations('global', ['setUseToastInterceptor']),
    ...mapMutations('savedLists', ['clearQty', 'setIsBuyingZone', 'identifyEntriesList']),
    ...mapActions('savedLists', [
      'fetchAllLists',
      'fetchList',
      'createList',
      'updateListEntries',
      'updateSelectedEntries',
      'deleteList',
    ]),
  },
};
</script>
